import { useState, useCallback } from 'react';
import { locationService } from '@modules/organizationalStructure/locations/services/locationService';
import { useMemo } from 'react';

const useLocations = () => {
    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [subRegions, setSubRegions] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedSubRegion, setSelectedSubRegion] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isRegionDisabled, setIsRegionDisabled] = useState(true);

    const countryOptions = useMemo(() => countries.map(country => ({
            value: country.countryCode,
            label: country.countryNameBg
        })), [countries]);
    
        const regionOptions = useMemo(() => regions.map(region => ({
            value: region.id,
            label: region.name
        })), [regions]);
    
        const subRegionOptions = useMemo(() => subRegions.map(subRegion => ({
            value: subRegion.id,
            label: subRegion.name
        })), [subRegions]);
    
        const cityOptions = useMemo(() => cities.map(city => ({
            value: city.id,
            label: city.name,
            postal: city.postal
        })), [cities]);
    

    const clearDependentSelections = () => {
        setRegions([]);
        setSubRegions([]);
        setCities([]);
        setSelectedRegion(null);
        setSelectedSubRegion(null);
        setSelectedCity(null);
        setIsRegionDisabled(true);
    };

    const fetchCountries = useCallback(async () => {
        try {
            const countriesData = await locationService.getCountries();
            setCountries(countriesData);
            
            // Set Bulgaria as the default selected country
            const bulgaria = countriesData.find(country => country.countryCode === 'BG');
            if (bulgaria) {
                setSelectedCountry(bulgaria.countryCode);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    }, []);

    const fetchRegionsByCountry = useCallback(async (countryCode) => {
        try {
            const regionsData = await locationService.getRegionsByCountry(countryCode);
            if (regionsData && regionsData.length > 0) {
                setRegions(regionsData);
                setIsRegionDisabled(false);
            } else {
                // Clear all dependent selections if country has no regions
                clearDependentSelections();
            }
        } catch (error) {
            console.error('Error fetching regions:', error);
            clearDependentSelections();
        }
    }, []);

    const fetchSubRegionsByRegion = useCallback(async (id) => {
        try {
            const subRegionsData = await locationService.getSubRegionsByRegion(id);
            if (subRegionsData && subRegionsData.length > 0) {
                setSubRegions(subRegionsData);
            } else {
                // Clear dependent selections
                setSubRegions([]);
                setCities([]);
                setSelectedSubRegion(null);
                setSelectedCity(null);
            }
        } catch (error) {
            console.error('Error fetching subregions:', error);
            setSubRegions([]);
            setCities([]);
            setSelectedSubRegion(null);
            setSelectedCity(null);
        }
    }, []);

    const fetchCitiesbySubRegion = useCallback(async (id) => {
        try {
            const citiesData = await locationService.getCitiesBySubRegion(id);
            if (citiesData && citiesData.length > 0) {
                setCities(citiesData);
            } else {
                setCities([]);
                setSelectedCity(null);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
            setSelectedCity(null);
        }
    }, []);

    return {
        countries,
        regions,
        subRegions,
        cities,
        fetchCountries,
        fetchRegionsByCountry,
        fetchSubRegionsByRegion,
        fetchCitiesbySubRegion,
        countryOptions,
        regionOptions,
        subRegionOptions,
        cityOptions,
        selectedCountry,
        selectedRegion,
        selectedSubRegion,
        setSelectedRegion,
        setSelectedSubRegion,
        setSelectedCountry,
        setSelectedCity,
        selectedCity,
        clearDependentSelections,
        isRegionDisabled
    };
};

export default useLocations;
